"use client";

import { useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { GUIDE_NAV } from "./deletion-steps";

const PRIMARY = "#112F6B";

/** Matches fixed SiteHeader (h-20) + small gap */
const STICKY_TOP = "top-24";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-48px" },
  transition: { duration: 0.45, delay, ease: "easeOut" as const },
});

function GuideSidebar({
  activeId,
  onNavigate,
}: {
  activeId: string;
  onNavigate: (id: string) => void;
}) {
  return (
    <nav aria-label="Guide sections">
      <p
        className="text-[11px] font-bold uppercase tracking-widest mb-4"
        style={{ color: PRIMARY }}
      >
        In this guide
      </p>
      <ul className="space-y-0.5 border-l-2 border-neutral-200">
        {GUIDE_NAV.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(item.id);
                }}
                className={`block py-2 pl-4 text-sm leading-snug border-l-2 -ml-[2px] transition-colors ${
                  isActive
                    ? "border-[#2BA6E6] font-semibold text-neutral-900"
                    : "border-transparent text-neutral-500 hover:text-neutral-800 hover:border-neutral-300"
                }`}
                aria-current={isActive ? "location" : undefined}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function GuideMobileNav({ activeId, onNavigate }: { activeId: string; onNavigate: (id: string) => void }) {
  return (
    <nav className="lg:hidden mb-8 -mx-1 overflow-x-auto" aria-label="Guide sections">
      <ul className="flex gap-2 pb-1 min-w-max px-1">
        {GUIDE_NAV.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(item.id);
                }}
                className={`inline-block whitespace-nowrap px-3.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  isActive
                    ? "border-[#2BA6E6] bg-blue-50/80 text-neutral-900"
                    : "border-neutral-200 bg-white text-neutral-600"
                }`}
                aria-current={isActive ? "location" : undefined}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Client island — scroll-spy navigation around the server-rendered steps,
 * which arrive as `children` so their copy and images stay in the HTML payload.
 */
export function GuideNavLayout({ children }: { children: ReactNode }) {
  const [activeId, setActiveId] = useState(GUIDE_NAV[0].id);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  };

  useEffect(() => {
    const ids = GUIDE_NAV.map((n) => n.id);
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el != null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      id="guide-layout"
      className="max-w-6xl mx-auto lg:flex lg:items-start lg:gap-10 xl:gap-14 !overflow-visible"
    >
      <aside
        className={`hidden lg:block w-[220px] xl:w-[240px] shrink-0 sticky ${STICKY_TOP} z-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain`}
      >
        <GuideSidebar activeId={activeId} onNavigate={scrollToSection} />
      </aside>

      <div className="min-w-0 flex-1 !overflow-visible">
        <GuideMobileNav activeId={activeId} onNavigate={scrollToSection} />

        <motion.div
          {...fadeUp(0.05)}
          style={{ overflow: "visible" }}
          className="bg-white rounded-xl border border-neutral-200 shadow-sm px-5 py-8 md:px-8 md:py-10 lg:px-10 lg:py-12"
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
}
